import type { Visualizer } from './types';
import { clearBg, drawHud } from './types';

/**
 * Joy-Division / ridge-plot spectrum — stacked frequency silhouettes.
 */
export const ridgeViz: Visualizer = {
  id: 'ridge',
  label: 'Ridge Plot',
  draw(frame) {
    const { ctx, w, h, freq, bands, t } = frame;
    clearBg(ctx, w, h, 1);

    const bg = ctx.createLinearGradient(0, 0, 0, h);
    bg.addColorStop(0, '#050a10');
    bg.addColorStop(1, '#0a121c');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);

    const rows = 28;
    const n = Math.min(120, freq.length);
    const left = w * 0.08;
    const right = w * 0.92;
    const top = h * 0.14;
    const bot = h * 0.88;
    const rowH = (bot - top) / rows;

    for (let r = rows - 1; r >= 0; r--) {
      const yBase = top + r * rowH;
      // each row samples a shifted window of spectrum + phase
      const phase = t * (0.4 + bands.energy * 0.5) + r * 0.15;
      ctx.beginPath();
      ctx.moveTo(left, bot + 20);
      ctx.lineTo(left, yBase);

      for (let i = 0; i < n; i++) {
        const u = i / (n - 1);
        const x = left + u * (right - left);
        // blend nearby bins; row prefers different bands
        const prefer = Math.floor((r / rows) * (freq.length * 0.7));
        const bin = Math.min(freq.length - 1, prefer + Math.floor(u * freq.length * 0.35));
        const v = freq[bin] / 255;
        const hump =
          v * rowH * (2.8 + bands.bass * 1.2) +
          Math.sin(u * Math.PI * 4 + phase) * rowH * 0.15 * (0.3 + bands.mids);
        const y = yBase - Math.max(0, hump);
        ctx.lineTo(x, y);
      }

      ctx.lineTo(right, yBase);
      ctx.lineTo(right, bot + 20);
      ctx.closePath();

      const depth = r / rows;
      ctx.fillStyle = `hsla(${175 + depth * 35 + bands.highs * 20}, 45%, ${8 + depth * 10}%, 0.92)`;
      ctx.fill();
      ctx.strokeStyle = `hsla(${165 + depth * 40}, 90%, ${45 + bands.energy * 20}%, ${0.25 + (1 - depth) * 0.45})`;
      ctx.lineWidth = 1.2 + bands.kick * 0.6 * (1 - depth);
      ctx.stroke();
    }

    // sparklines of true wave at bottom
    ctx.beginPath();
    const ww = frame.wave;
    for (let i = 0; i < ww.length; i += 4) {
      const x = left + (i / ww.length) * (right - left);
      const y = bot + 8 + (ww[i] || 0) * 28 * (1 + bands.energy);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.strokeStyle = `rgba(86,240,200,${0.25 + bands.energy * 0.35})`;
    ctx.lineWidth = 1.5;
    ctx.stroke();

    drawHud(frame);
  },
};
